import React, { useCallback, useMemo, useState } from "react";
import { Popover } from "@blueprintjs/core";
import { useSelector } from "react-redux";
import IconButton from "components/IconButton";
import Button, { ButtonSize, ButtonStyle } from "components/Button";
import DefaultThreeDotsMenuItem from "components/ThreeDotsMenu/Components/DefaultThreeDotsMenuItem";
import { EngineResultType, PsGlobalEntityType, ScenarioEntityType, } from "store/ps-workspace/ps-workspace.types";
import { deleteTargetSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { scrollElementSelector } from "store/global/global.selectors";
import { useOnScrollPopoverStateListener } from "hooks/useScrollDebounceListener";
import DeleteIcon from "icons/delete.svg";
import * as styles from "./styles.module.scss";
const SimpleRemoveButton = ({ action, onOpen, disabled, className, isMenuItem = true, withConfirmation = true, customButtonText, customConfirmationText, scrollElement, }) => {
    const [showConfirmationDialog, setConfirmationDialogState] = useState(false);
    const [popoverRef, setPopoverRef] = useState(null);
    const deleteTarget = useSelector(deleteTargetSelector);
    const elementScroll = useSelector(scrollElementSelector(scrollElement));
    const clickHandler = useCallback((e) => {
        e.stopPropagation();
        if (withConfirmation) {
            setConfirmationDialogState(true);
            onOpen && onOpen();
        }
        else {
            action(true);
        }
    }, [action, onOpen, withConfirmation]);
    const confirmationHandler = useCallback((isConfirmed, e) => {
        e?.stopPropagation();
        setConfirmationDialogState(false);
        action(isConfirmed);
    }, [action]);
    const onInteraction = useCallback((nextOpenState) => {
        if (showConfirmationDialog && !nextOpenState) {
            confirmationHandler(false);
        }
    }, [confirmationHandler, showConfirmationDialog]);
    const elementTypeLabel = useMemo(() => {
        switch (deleteTarget?.type) {
            case PsGlobalEntityType.ResultSet:
                return "Result Set";
            case ScenarioEntityType.SimpleMetric:
            case ScenarioEntityType.RelativeMetric:
                return "Metric";
            case EngineResultType.Result0D:
                return "Tabular view";
            case EngineResultType.Result1D:
                return "Line Graph";
            case EngineResultType.Result2D:
                return "Heatmap";
            case EngineResultType.ResultMetadata:
                return "Metadata";
            default:
                return deleteTarget?.type;
        }
    }, [deleteTarget]);
    const confirmationDialog = useMemo(() => {
        if (deleteTarget === null || !deleteTarget.type) {
            return React.createElement("div", null);
        }
        if (deleteTarget.type) {
            return (React.createElement("div", { className: styles.confirmationDialog, onClick: (e) => e?.stopPropagation() },
                React.createElement("div", { className: styles.content },
                    React.createElement("div", { className: styles.label }, customConfirmationText || `Are you sure you want to delete this ${elementTypeLabel}?`),
                    React.createElement("div", { className: styles.container },
                        React.createElement(Button, { onClick: (e) => confirmationHandler(false, e), label: "No", role: "button", style: ButtonStyle.Secondary, size: ButtonSize.Normal, disabled: disabled }),
                        React.createElement(Button, { onClick: (e) => confirmationHandler(true, e), label: "Yes", role: "button", style: ButtonStyle.Primary, size: ButtonSize.Normal, disabled: disabled })))));
        }
    }, [deleteTarget, customConfirmationText, elementTypeLabel, disabled, confirmationHandler]);
    useOnScrollPopoverStateListener(showConfirmationDialog, onInteraction, popoverRef, elementScroll);
    return (React.createElement(Popover, { fill: true, minimal: true, placement: "left", isOpen: showConfirmationDialog, popoverClassName: styles.popover, className: className, content: confirmationDialog, onInteraction: onInteraction, popoverRef: setPopoverRef }, isMenuItem ? (React.createElement(DefaultThreeDotsMenuItem, { text: customButtonText ?? "Delete", icon: React.createElement(DeleteIcon, null), disabled: disabled, onClick: clickHandler, classes: { iconClassName: styles["threeDotsMenu-icon"] } })) : (React.createElement(IconButton, { icon: React.createElement(DeleteIcon, null), title: "Delete", onClick: clickHandler }))));
};
export default SimpleRemoveButton;
